import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { MediaRegistry } from '../registries/MediaRegistry';
import type { ToolExecutionContext } from '../types';

import { Tool } from './Tool';

import { MediaSearchSourceEnum, WorkspaceMediaModeEnum } from 'js/src/client';
import { ApiService } from 'js/src/client/services.gen';
import { MEDIA_LICENSE_OPTIONS } from 'js/src/data/media';
import { selectMediaOptions } from 'js/src/data/media-recommendation-preferences';

const RESULT_COUNT = 10;

export const mediaSearchSchema = z.object({
    query: z
        .string()
        .optional()
        .describe(
            'The search query to find relevant media. Works best with detailed queries. Must be English. Required when source is "library". Optional when source is "uploads" — omit it to list the user\'s most recent uploads, or provide a query to filter them by name.',
        ),
    media_type: z
        .enum(['images', 'videos', 'stickers', 'all'])
        .default('all')
        .describe(
            'Type of media to search for. When searching stock for scene media, default to "videos" — video keeps scenes moving, and a stock photo is a last resort for when repeated video searches from different angles turn up nothing relevant. Use "images", "stickers", or "all" when the user asks for them or the content genuinely calls for a still. Note: stickers are animated and will have type "video" in results.',
        ),
    source: z
        .enum(['library', 'uploads'])
        .default('library')
        .describe(
            'Where to search. "library" (default) searches stock/library media. "uploads" searches only the media the user has previously uploaded to their account.',
        ),
});
export type MediaSearchArgs = z.infer<typeof mediaSearchSchema>;

export class MediaSearchTool extends Tool<typeof mediaSearchSchema> {
    protected override readonly schema = mediaSearchSchema;
    protected override readonly toolName = TOOL_NAMES.searchMedia;
    protected override readonly toolDescription =
        'Search for media assets (images, videos, stickers, or all types) using a text query. By default this searches the stock/library media.';

    public override canRunAsync = true;

    public override getToolDisplayName(): string {
        return 'Searching for media';
    }

    public get statusIcon(): string {
        return 'perm_media';
    }

    public async execute(args: MediaSearchArgs, _context: ToolExecutionContext) {
        const { media_type = 'all', source = 'library' } = args;
        const query = args.query?.trim() ?? '';

        if (source !== 'uploads' && !query) {
            return 'Error: a search query is required when searching the media library. Provide a descriptive query, or set source to "uploads" to browse the user\'s uploaded media.';
        }

        // Respect the user's media-source preferences: don't search a source the
        // user has disabled. Return a message rather than results so the agent can
        // switch to an enabled source.
        const mediaOptions = selectMediaOptions(this._getState());
        if (source === 'uploads') {
            if (mediaOptions.workspace_media_mode === WorkspaceMediaModeEnum.OFF) {
                return 'Workspace/uploaded media is disabled by the user\'s media preferences. Search stock media with source "library" if it is enabled, or ask the user to enable workspace media.';
            }
        } else if (!mediaOptions.stock_media_enabled) {
            return 'Stock media is disabled by the user\'s media preferences. Use source "uploads" for the user\'s own media, or ask the user to enable workspace media.';
        }

        try {
            // Set up search source and filter based on media type
            let searchSource: MediaSearchSourceEnum;
            let filter = '';

            if (source === 'uploads') {
                // Search only the user's own uploaded media. License/marketplace filters
                // are meaningless for the user's own uploads, so we only apply the type filter.
                searchSource = MediaSearchSourceEnum.UPLOADS;
                if (media_type === 'images') {
                    filter = 'photos';
                } else if (media_type === 'videos') {
                    filter = 'videos';
                } else {
                    filter = 'photos,videos';
                }
            } else if (media_type === 'stickers') {
                searchSource = MediaSearchSourceEnum.FREEPIK_STICKERS;
                filter = 'basic'; // Minimal filter for stickers
            } else {
                searchSource = MediaSearchSourceEnum.RECOMMENDED;
                if (media_type === 'images') {
                    filter = 'photos';
                } else if (media_type === 'videos') {
                    filter = 'videos';
                } else {
                    filter = 'photos,videos';
                }
                filter += ',' + MEDIA_LICENSE_OPTIONS.EDITORIAL + ',' + MEDIA_LICENSE_OPTIONS.STOCK;
                filter += ',basic,premium,marketplace'; // TODO: refine this based on user
            }

            const response = await ApiService.mediaSearchCreate({
                body: {
                    q: query,
                    search_source: searchSource,
                    page: 1,
                    count: RESULT_COUNT,
                    filter,
                    autocorrect: false,
                    did_you_mean: false,
                },
            });

            // Add results to MediaRegistry and return representations
            const mediaRegistry = MediaRegistry.getInstance();
            const representations = response.data.results.map((result) => {
                const id = mediaRegistry.addItem(result);
                return mediaRegistry.getRepresentation(id);
            });

            return JSON.stringify(representations);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error searching for media: ${errorMessage}`;
        }
    }
}
